import type { VapiMessage } from '@/features/calls/types'

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-'
  const rounded = Math.round(seconds)
  if (rounded < 60) return `${rounded}s`
  const mins = Math.floor(rounded / 60)
  const secs = rounded % 60
  return `${mins}m ${secs}s`
}

export function formatDateTime(value: string | null): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export function formatCost(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return `$${value.toFixed(4)}`
}

export function formatPhone(phone: string | null): string {
  if (!phone) return '-'
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return phone
}

export function formatShortDateTime(value: string | null): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

export function formatSeconds(secs: number | undefined): string {
  if (secs === undefined) return '-'
  const m = Math.floor(secs / 60)
  const s = Math.floor(secs % 60)
  const ms = Math.round((secs % 1) * 1000)
  if (m > 0)
    return `${m}:${String(s).padStart(2, '0')}.${String(ms).padStart(3, '0')}`
  return `${s}.${String(ms).padStart(3, '0')}s`
}

/**
 * Millisecond-less `m:ss` / `Ns` duration label, e.g. for a tool-call or
 * auth-attempt timestamp relative to call start. Distinct from `formatSeconds`
 * above, which appends milliseconds for transcript-turn timing precision.
 */
export function formatDurationShort(secs: number | undefined): string {
  if (secs === undefined) return '-'
  const m = Math.floor(secs / 60)
  const s = Math.floor(secs % 60)
  if (m > 0) return `${m}:${String(s).padStart(2, '0')}`
  return `${s}s`
}

export function findActiveMessageIndex(
  messages: VapiMessage[],
  currentTime: number
): number {
  if (messages.length === 0) return -1
  const first = messages[0]!
  if (currentTime < first.secondsFromStart) return -1
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]!
    if (msg.secondsFromStart <= currentTime) return i
  }
  return -1
}
